/** @jsx jsx */
import React from 'react';
import { css, styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import some from 'lodash/some';
import toLower from 'lodash/toLower';
import trim from 'lodash/trim';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import ErrorIcon from '@atlaskit/icon/core/error';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/chevron-up';
import InlineDialog from '@atlaskit/inline-dialog';
import InlineEdit from '@atlaskit/inline-edit';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { messages as dragIndicatorMessages } from '../../../common/messages';
import type { Group } from '../../../common/types';
import { TooltipTrigger, DragProxy } from '../../../common/ui/mouse-activity-proxy/index.tsx';
import ButtonProxy from '../../../common/ui/mouse-activity-proxy/main.tsx';
import { truncatableTitleStyles } from '../../../common/ui/styled.tsx';
import useGroupList from '../../../controllers/groups-resource/index.tsx';
import messages from './messages';

const GROUP_NAME_MAX_LENGTH = 128;

export type Props = {
	isOpen: boolean;
	hasOpen: boolean;
	hasReorder: boolean;
	hasRemove: boolean;
	canOpen: boolean;
	name: string;
	groupId: number;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	onOpen: () => void;
	onRemove: () => void;
	onUpdate: (arg1: string) => void;
};

export const validateDuplicateGroupName = (
	value: string,
	groupId: number,
	groupsList: Group[],
	formatMessage: (messageDescriptor: MessageDescriptor) => string,
) => {
	if (
		some(
			groupsList,
			(group) => group.id !== groupId && toLower(trim(value)) === toLower(trim(group.name)),
		)
	) {
		return formatMessage(messages.groupNameDuplicateError);
	}
	return undefined;
};

const GroupHeader = ({
	isOpen,
	hasOpen,
	canOpen,
	hasReorder,
	hasRemove,
	name,
	groupId,
	onOpen,
	onRemove,
	onUpdate,
	dragHandleProps,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const groupsList = useGroupList();
	const ariaPropsForDragButton = {
		'aria-labelledby': undefined,
		'aria-label': formatMessage(dragIndicatorMessages.draggableItemIndicatorAccessibilityLabel),
	};

	const dragButtonProps = { ...dragHandleProps, ...ariaPropsForDragButton };

	return (
		<Wrapper>
			<DragButton {...dragButtonProps} hidden={!hasReorder}>
				<DragHandlerIcon
					label={formatMessage(dragIndicatorMessages.draggableItemIndicatorAccessibilityLabel)}
				/>
			</DragButton>
			<EditorWrapper data-testid="servicedesk-portal-settings-request-groups.ui.portal-group.group-header.group-title">
				<InlineEdit
					readViewFitContainerWidth
					startWithEditViewOpen={!name}
					defaultValue={name}
					editButtonLabel={formatMessage(messages.editButtonLabel, {
						groupName: name,
					})}
					editView={({ errorMessage, ...fieldProps }) => (
						<GetInputThemeWrapper>
							<InlineDialog
								isOpen={fieldProps.isInvalid}
								content={<div id="error-message">{errorMessage}</div>}
								placement="right"
							>
								<Textfield
									maxLength={GROUP_NAME_MAX_LENGTH}
									{...fieldProps}
									autoFocus
									autoComplete="off"
									placeholder={formatMessage(messages.inputPlaceholder)}
									elemAfterInput={
										fieldProps.isInvalid && (
											<ErrorIconWrapper>
												<ErrorIcon label="error" color={token('color.icon.danger', R400)} />
											</ErrorIconWrapper>
										)
									}
								/>
							</InlineDialog>
						</GetInputThemeWrapper>
					)}
					readView={() => (
						<Title title={name}>{name || formatMessage(messages.inputPlaceholder)}</Title>
					)}
					onConfirm={(value) => {
						const trimValue = value.trim();
						trimValue !== name && onUpdate(trimValue);
					}}
					validate={(value) =>
						validateDuplicateGroupName(value, groupId, groupsList, formatMessage)
					}
				/>
			</EditorWrapper>
			<RemoveButton
				data-testid="servicedesk-portal-settings-request-groups.ui.portal-group.group-header.delete-button"
				aria-label={formatMessage(messages.removeGroupButton, { groupName: name })}
				onClick={() => {
					fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'groupDelete');
					onRemove();
				}}
				hidden={!hasRemove}
			>
				<Tooltip content={formatMessage(messages.removeIconTooltip)} position="top">
					<TooltipTrigger tabIndex={-1} />
				</Tooltip>
				<TrashIcon label="" />
			</RemoveButton>
			<ExpandButton
				data-testid="servicedesk-portal-settings-request-groups.ui.portal-group.group-header.expand-button"
				aria-label={formatMessage(messages.openGroupButtonAria, { groupName: name })}
				aria-expanded={isOpen}
				onClick={() => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						'button clicked',
						isOpen ? 'groupCollapse' : 'groupExpand',
					);
					onOpen();
				}}
				disabled={!canOpen}
				hidden={!hasOpen}
			>
				{isOpen ? (
					<ChevronUpIcon label="" color="currentColor" />
				) : (
					<ChevronDownIcon label="" color="currentColor" />
				)}
			</ExpandButton>
		</Wrapper>
	);
};

GroupHeader.defaultProps = {
	isOpen: false,
	hasOpen: true,
	canOpen: true,
	hasReorder: true,
	hasRemove: true,
	onOpen: noop,
	onRemove: noop,
	onUpdate: noop,
};

export default GroupHeader;

const wrapperSelectorValue = 'portal-settings-request-groups.portal-group.group-header.wrapper';
const wrapperSelector = `[data-component-selector="${wrapperSelectorValue}"]`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Wrapper = (props: any) => (
	<div {...props} css={wrapperExperimentStyles} data-component-selector={wrapperSelectorValue} />
);

const wrapperExperimentStyles = css({
	display: 'flex',
	alignItems: 'center',
	height: '40px',
});

const removeButtonSelectorValue =
	'portal-settings-request-groups.portal-group.group-header.delete-button';
export const removeButtonSelector = `[data-component-selector="${removeButtonSelectorValue}"]`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RemoveButton = (props: any) => (
	<ButtonProxy
		{...props}
		css={[
			commonButtonStylesExperimentStyles,
			buttonHoverStylesExperimentStyles,
			removeButtonExperimentStyles,
			props.hidden && removeButtonHiddenStyles,
		]}
		data-component-selector={removeButtonSelectorValue}
	/>
);

const removeButtonExperimentStyles = css({
	width: '40px',
	opacity: 0,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`${wrapperSelector}:hover &`]: {
		opacity: 1,
	},
});

const removeButtonHiddenStyles = css({
	visibility: 'hidden',
});

const commonButtonStylesExperimentStyles = css({
	transition: 'opacity 0.5s',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	zIndex: 0,
});

const buttonHoverStylesExperimentStyles = css({
	'&:hover, &:focus': {
		'&::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.neutral.subtle.hovered', colors.N30),
			borderRadius: '3px',
			content: "''",
			zIndex: -1,
			width: '32px',
			height: '32px',
			position: 'absolute',
		},
	},
});

const TITLE_FONT_SIZE = 20;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DragButton = styled(DragProxy)<{ hidden: boolean }>({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ hidden }) => (hidden ? 'hidden' : 'visible'),
	width: '28px',
	flexShrink: 0,

	// Prevent style override by global styles
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&[hidden]': {
		display: 'block',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorWrapper = styled.div({
	flexGrow: 1,
	/* Restrict atlaskit Inline edit */
	width: 0,
	/* Negate Inline edit */
	marginTop: token('space.negative.100', '-8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...truncatableTitleStyles,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: `${TITLE_FONT_SIZE}px`,
	padding: token('space.050', '4px'),
	height: '20px',
});

const commonButtonStyles = css({
	transition: 'opacity 0.5s',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ hidden }: { hidden: boolean }) => (hidden ? 'hidden' : 'visible'),
	zIndex: 0,
});

const buttonHoverStyles = css({
	'&:hover, &:focus': {
		'&::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.neutral.subtle.hovered', colors.N30),
			borderRadius: '3px',
			content: "''",
			zIndex: -1,
			width: '32px',
			height: '32px',
			position: 'absolute',
		},
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ExpandButton = styled(ButtonProxy)<any>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...commonButtonStyles,
	width: '56px',
	opacity: 1,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ disabled }) =>
		disabled
			? {
					opacity: 0.5,
					cursor: 'initial',
				}
			: buttonHoverStyles,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GetInputThemeWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'input[data-ds--text-field--input]': {
		height: 'auto',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: `${TITLE_FONT_SIZE}px`,
		padding: token('space.050', '4px'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> [data-ds--text-field--container]': {
		height: 'auto',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorIconWrapper = styled.div({
	paddingRight: token('space.075', '6px'),
	lineHeight: "'100%'",
});
